import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { reactQueryCacheAndRefetchOptions } from "@";
import { useUrlSearch } from "@/shared/hooks/useUrlSearch";
import { getDonations } from "./api";
import { searchValues } from "./options";

// Types
interface DonationsSearchValues {
  id: string;
  isPaid: string;
  toDate: Date | null;
  toAmount: string;
  fromDate: Date | null;
  fromAmount: string;
  phoneNumber: string;
  paymentType: string;
  orderStatus: string;
  orderType: string;
  clientId: string;
}

interface PaginationState {
  page: number;
  size: number;
}

interface UseDonationsListPageReturn {
  data: any[];
  isLoading: boolean;
  error: Error | null;
  totalItems: number;
  // Search
  search: { show: boolean; values: DonationsSearchValues };
  updateSearch: (params: {
    show?: boolean;
    values?: Partial<DonationsSearchValues>;
  }) => void;
  handleSearchSubmit: () => void;
  handleSearchReset: () => void;
  // Pagination
  pg: PaginationState & { rows: number };
  updatePg: (newPg: Partial<PaginationState>) => void;
  // Actions
  refetch: () => void;
  setData: React.Dispatch<React.SetStateAction<any[]>>;
}

// Query keys
export const donationsKeys = {
  all: ["donations-list"] as const,
  list: (params: { page: number; size: number; search: DonationsSearchValues }) =>
    [...donationsKeys.all, "list", params] as const,
  activity: (params: { orderId: number | string; page: number; pageSize: number }) =>
    [...donationsKeys.all, "activity", params] as const,
};

export const useDonationsListPage = (): UseDonationsListPageReturn => {
  const {
    appliedValues,
    draftValues,
    updateDraft,
    applySearch,
    resetSearch,
    page,
    size,
    setPage,
    setSize,
    showFilters,
    setShowFilters,
  } = useUrlSearch<DonationsSearchValues>({
    defaultValues: searchValues as DonationsSearchValues,
  });

  // Query with URL-based params
  const queryParams = useMemo(
    () => ({
      page,
      size,
      search: appliedValues,
    }),
    [page, size, appliedValues],
  );

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: donationsKeys.list(queryParams),
    queryFn: async () => {
      const response = await getDonations(page, size, appliedValues);
      return response.data;
    },
    ...reactQueryCacheAndRefetchOptions,
  });

  const data = queryData?.data || [];
  const totalItems = queryData?.totalItems || 0;

  // Handlers
  const updatePg = useCallback(
    (newPg: Partial<PaginationState>) => {
      if (newPg.page !== undefined) setPage(newPg.page);
      if (newPg.size !== undefined) setSize(newPg.size);
    },
    [setPage, setSize],
  );

  const updateSearch = useCallback(
    (updates: { show?: boolean; values?: Partial<DonationsSearchValues> }) => {
      if (updates.show !== undefined) setShowFilters(updates.show);
      if (updates.values) {
        Object.entries(updates.values).forEach(([key, value]) => {
          updateDraft(key as keyof DonationsSearchValues, value as any);
        });
      }
    },
    [setShowFilters, updateDraft],
  );

  // Placeholder setData for compatibility with existing code
  const setData = useCallback(() => {
    // Trigger refetch instead of manual data manipulation
    refetch();
  }, [refetch]);

  return {
    data,
    isLoading,
    error: error as Error | null,
    totalItems,
    // Search (for compatibility with existing components)
    search: { show: showFilters, values: draftValues },
    updateSearch,
    handleSearchSubmit: applySearch,
    handleSearchReset: resetSearch,
    // Pagination (for compatibility with existing components)
    pg: { page, size, rows: totalItems },
    updatePg,
    // Actions
    refetch,
    setData: setData as any,
  };
};
